import * as React from 'react';
import isEqual from 'lodash.isequal';
import Button from '../button';
import ButtonGroup from '../button_group/button_group';
import IconArrowDown from '../icons/icon_arrow_down';
import IconArrowUp from '../icons/icon_arrow_up';
import IconGear from '../icons/icon_gear';
import Modal from '../modal';
import { useColumnOptionsStyles } from './use_styles';
import useTableContext from '../table/use_table_context';
import scrollEnded from '../utils/scroll_ended';
import ColumnOptionsList from './column_options_list';
import Scrollbar from '../scrollbar';
import { jsx as ___EmotionJSX } from "@emotion/core";
var ColumnOptions = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {} = _ref;
  var {
    columnReorderFooterAlign,
    columnReorderInfiniteScroll,
    columnReorderPortalContainer,
    columns: orderedColumns,
    columnsConfig: columnsProp,
    defaultOrder,
    disableResetToDefault,
    i18n,
    onColumnReorder,
    onResetToDefault,
    order,
    theme
  } = useTableContext();
  var handleIndexColumnVisibility = mergedColumns => mergedColumns.filter(c => c.index !== 'hidden');
  var mergedColumns = React.useMemo(() => {
    return [...new Set([...orderedColumns, ...columnsProp])].filter(c => c.index !== 'hidden');
  }, [columnsProp, orderedColumns]);
  var MAX_VISIBLE_OPTIONS = 14;
  var renderedMergedColumns = React.useMemo(() => {
    return columnReorderInfiniteScroll ? mergedColumns.slice(0, MAX_VISIBLE_OPTIONS) : mergedColumns;
  }, [columnReorderInfiniteScroll, mergedColumns]);
  var [columnOptions, setColumnOptions] = React.useState(renderedMergedColumns);
  var [selectedColumn, setSelectedColumn] = React.useState();
  var [sessionOptions, setSessionOptions] = React.useState(renderedMergedColumns);
  var [showColumnOptions, setShowColumnOptions] = React.useState(false);
  var [checkedColumns, setCheckedColumns] = React.useState(order);
  var sliceNumber = React.useRef(MAX_VISIBLE_OPTIONS);

  // Returns visible columns from sessionOptions
  var visibleSessionOptions = React.useMemo(() => sessionOptions.filter(s => defaultOrder === null || defaultOrder === void 0 ? void 0 : defaultOrder.includes(s.id)), [defaultOrder, sessionOptions]);
  React.useEffect(() => {
    setCheckedColumns(checkedColumns => {
      var orderChanged = order.length !== checkedColumns.length || !order.every((value, index) => value === checkedColumns[index]);
      if (orderChanged) {
        return [...order];
      }
      return checkedColumns;
    });
  }, [order]);
  React.useEffect(() => {
    var newOptions = sessionOptions.filter(column => columnsProp.findIndex(c => c.id === column.id) > -1);
    setColumnOptions([...newOptions]);
  }, [columnsProp, sessionOptions]);
  var columnOptionsStyle = useColumnOptionsStyles({
    theme
  });
  var handleCancel = React.useCallback(() => {
    setShowColumnOptions(false);
    setSelectedColumn(undefined);
    setCheckedColumns([...order]);
  }, [order]);
  var handleClick = React.useCallback(option => {
    if (option.id !== selectedColumn) setSelectedColumn(option.id);
  }, [selectedColumn]);
  var handleSave = React.useCallback(() => {
    if (onColumnReorder) {
      var reduceCheckedColumns = (orderList, column) => {
        var isChecked = checkedColumns.includes(column.id) || column.required;
        if (isChecked) orderList.push(column.id);
        return orderList;
      };
      var newColumnsOrder = sessionOptions.reduce(reduceCheckedColumns, []);
      onColumnReorder(newColumnsOrder);
    }
    setShowColumnOptions(false);
    setColumnOptions(sessionOptions);
  }, [checkedColumns, onColumnReorder, sessionOptions]);
  var handleCheckbox = React.useCallback(option => {
    if (option.required) return;
    setCheckedColumns(prev => {
      var index = prev.findIndex(id => id === option.id);
      var newCheckedColumns = [...prev];
      if (index === -1) {
        newCheckedColumns.push(option.id);
      } else {
        newCheckedColumns.splice(index, 1);
      }
      return newCheckedColumns;
    });
  }, []);
  var swapPositions = React.useCallback((init, target) => {
    var columnsNewOrder = sessionOptions;
    [columnsNewOrder[init], columnsNewOrder[target]] = [sessionOptions[target], sessionOptions[init]];
    setSessionOptions([...columnsNewOrder]);
  }, [sessionOptions]);
  var handleUpMove = React.useCallback(() => {
    if (!selectedColumn) return;
    var index = sessionOptions.findIndex(c => c.id === selectedColumn);
    if (index === 0) return;
    swapPositions(index, index - 1);
  }, [selectedColumn, sessionOptions, swapPositions]);
  var handleDownMove = React.useCallback(() => {
    if (!selectedColumn) return;
    var index = sessionOptions.findIndex(c => c.id === selectedColumn);
    if (index === sessionOptions.length - 1) return;
    swapPositions(index, index + 1);
  }, [selectedColumn, sessionOptions, swapPositions]);
  var handleReset = React.useCallback(() => {
    var defaultColumns = (defaultOrder || []).map(value => columnsProp.find(c => c.id === value)).filter(Boolean);
    var resetColumns = [...new Set([...defaultColumns, ...columnsProp])];
    if (columnReorderInfiniteScroll) {
      resetColumns = resetColumns.slice(0, sliceNumber.current);
    }
    setCheckedColumns([...(defaultOrder || [])]);
    setSelectedColumn(undefined);
    setSessionOptions(handleIndexColumnVisibility(resetColumns));
    onResetToDefault === null || onResetToDefault === void 0 || onResetToDefault();
  }, [columnReorderInfiniteScroll, columnsProp, defaultOrder, onResetToDefault]);
  var handleKeyDown = React.useCallback((e, option) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.stopPropagation();
        if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
          handleCheckbox(option);
        } else {
          handleClick(option);
        }
        break;
      default:
        break;
    }
  }, [handleCheckbox, handleClick]);
  var handleModalKeyDown = React.useCallback(e => {
    if (e.key === 'Escape') handleCancel();
  }, [handleCancel]);
  var listRef = React.useRef();
  var moveButtonsRef = React.useRef(null);
  var handleOpenButtonClick = React.useCallback(() => {
    setSessionOptions([...new Set([...columnOptions])]);
    setShowColumnOptions(true);
  }, [columnOptions]);
  var handleOnScrollEnd = React.useCallback(event => {
    if (columnReorderInfiniteScroll && scrollEnded(event.target, -2)) {
      var renderMoreOptions = mergedColumns.slice(0, sliceNumber.current + MAX_VISIBLE_OPTIONS);
      setSessionOptions(renderMoreOptions);
      sliceNumber.current += MAX_VISIBLE_OPTIONS;
    }
  }, [columnReorderInfiniteScroll, mergedColumns]);
  var shouldDisableArrow = React.useCallback(direction => {
    if (!columnOptions) return false;
    if (!selectedColumn) return true;
    var firstOption = sessionOptions[0].id;
    var lastOption = sessionOptions[sessionOptions.length - 1].id;
    if (direction === 'up' && selectedColumn === firstOption) {
      return true;
    }
    return direction === 'down' && selectedColumn === lastOption;
  }, [columnOptions, selectedColumn, sessionOptions]);
  React.useEffect(() => {
    var handleClickOutside = event => {
      var _listRef$current, _moveButtonsRef$curre;
      if ((_listRef$current = listRef.current) !== null && _listRef$current !== void 0 && _listRef$current.contains(event.target) || (_moveButtonsRef$curre = moveButtonsRef.current) !== null && _moveButtonsRef$curre !== void 0 && _moveButtonsRef$curre.contains(event.target)) {
        return;
      }
      setSelectedColumn(undefined);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColumnOptions]);
  var footer = ___EmotionJSX(ButtonGroup, null, ___EmotionJSX(Button, {
    css: columnOptionsStyle.saveButton,
    onClick: handleSave
  }, i18n.saveButton), ___EmotionJSX(Button, {
    appearance: "secondary",
    onClick: handleCancel
  }, i18n.cancelButton));
  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(Button, {
    appearance: "ghost",
    "aria-label": i18n.columnOptions,
    css: columnOptionsStyle.button,
    leadingIcon: ___EmotionJSX(IconGear, null),
    onClick: handleOpenButtonClick,
    ref: ref
  }), showColumnOptions && ___EmotionJSX(Modal, {
    alignFooter: columnReorderFooterAlign,
    footer: footer,
    onClose: handleCancel,
    onKeyDown: handleModalKeyDown,
    portalContainer: columnReorderPortalContainer,
    show: showColumnOptions,
    title: i18n.columnOptions,
    id: "column-options"
  }, ___EmotionJSX("div", null, ___EmotionJSX("div", {
    css: columnOptionsStyle.panel
  }, ___EmotionJSX(Scrollbar, {
    customStyle: columnOptionsStyle.list,
    onScroll: handleOnScrollEnd,
    ref: listRef
  }, ___EmotionJSX(ColumnOptionsList, {
    checkedColumns: checkedColumns,
    theme: theme,
    options: sessionOptions,
    handleCheckbox: handleCheckbox,
    handleClick: handleClick,
    handleKeyDown: handleKeyDown,
    selectedColumn: selectedColumn
  })), ___EmotionJSX("div", {
    css: columnOptionsStyle.moveButtons,
    ref: moveButtonsRef
  }, ___EmotionJSX(Button, {
    appearance: "ghost",
    "aria-label": i18n.upButton,
    disabled: shouldDisableArrow('up'),
    leadingIcon: ___EmotionJSX(IconArrowUp, null),
    onClick: handleUpMove,
    small: true
  }), ___EmotionJSX(Button, {
    appearance: "ghost",
    "aria-label": i18n.downButton,
    disabled: shouldDisableArrow('down'),
    leadingIcon: ___EmotionJSX(IconArrowDown, null),
    onClick: handleDownMove,
    small: true
  }))), ___EmotionJSX(Button, {
    appearance: "ghost",
    css: columnOptionsStyle.reset,
    className: "vn-reset-to-default-button",
    disabled: disableResetToDefault !== undefined ? disableResetToDefault : isEqual(defaultOrder, checkedColumns) && isEqual(defaultOrder, visibleSessionOptions.map(c => c.id)),
    onClick: handleReset,
    small: true
  }, i18n.resetToDefault))));
});
ColumnOptions.displayName = 'ColumnOptions';
export default ColumnOptions;